import { z } from "zod";
import { controlKeySchema, settlementSchema } from "../counterparties/counterparties.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
const queryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const reasonSchema = z.string().trim().min(3).max(500);
// ── Sabitler ve etiketler ────────────────────────────────────────────────────
export const INSTRUMENT_KINDS = ["CHEQUE", "PROMISSORY_NOTE"];
export const instrumentKindSchema = z.enum(INSTRUMENT_KINDS);
export const INSTRUMENT_KIND_LABELS = {
    CHEQUE: "Çek",
    PROMISSORY_NOTE: "Senet",
};
export const INSTRUMENT_DIRECTIONS = ["RECEIVED", "ISSUED"];
export const instrumentDirectionSchema = z.enum(INSTRUMENT_DIRECTIONS);
export const INSTRUMENT_DIRECTION_LABELS = {
    RECEIVED: "Alınan (müşteri)",
    ISSUED: "Verilen (kendi)",
};
export const RECEIVED_INSTRUMENT_STATUSES = [
    "IN_PORTFOLIO",
    "IN_COLLECTION",
    "AS_COLLATERAL",
    "ENDORSED",
    "CLOSED",
    "RETURNED_BOUNCED",
    "RETURNED_TO_CUSTOMER",
    "COLLECTED",
    "BOUNCED",
    "LEGAL_FOLLOWUP",
    "WRITTEN_OFF",
    "PROTESTED",
];
export const ISSUED_INSTRUMENT_STATUSES = ["ISSUED", "PAID", "CANCELLED"];
export const INSTRUMENT_STATUSES = [...RECEIVED_INSTRUMENT_STATUSES, ...ISSUED_INSTRUMENT_STATUSES];
export const instrumentStatusSchema = z.enum(INSTRUMENT_STATUSES);
export const INSTRUMENT_STATUS_LABELS = {
    IN_PORTFOLIO: "Portföyde",
    IN_COLLECTION: "Bankada tahsilde",
    AS_COLLATERAL: "Bankada teminatta",
    ENDORSED: "Ciro edildi",
    CLOSED: "Kapandı (ciro riski bitti)",
    RETURNED_BOUNCED: "Karşılıksız geri geldi",
    RETURNED_TO_CUSTOMER: "Müşteriye iade edildi",
    COLLECTED: "Tahsil edildi",
    BOUNCED: "Karşılıksız",
    LEGAL_FOLLOWUP: "Yasal takipte",
    WRITTEN_OFF: "Şüpheli alacaktan silindi",
    PROTESTED: "Protesto edildi",
    ISSUED: "Keşide edildi",
    PAID: "Ödendi",
    CANCELLED: "İptal edildi",
};
/** Portföy listesinin durum sekmeleri. */
export const INSTRUMENT_STATUS_GROUPS = {
    PORTFOLIO: ["IN_PORTFOLIO"],
    AT_BANK: ["IN_COLLECTION", "AS_COLLATERAL"],
    ENDORSED: ["ENDORSED"],
    PROBLEM: ["BOUNCED", "RETURNED_BOUNCED", "PROTESTED", "LEGAL_FOLLOWUP"],
    CLOSED: ["COLLECTED", "CLOSED", "RETURNED_TO_CUSTOMER", "WRITTEN_OFF"],
    ISSUED_OPEN: ["ISSUED"],
    ISSUED_CLOSED: ["PAID", "CANCELLED"],
};
export const instrumentStatusGroupSchema = z.enum(Object.keys(INSTRUMENT_STATUS_GROUPS));
export const INSTRUMENT_STATUS_GROUP_LABELS = {
    PORTFOLIO: "Portföyde",
    AT_BANK: "Bankada",
    ENDORSED: "Ciroda",
    PROBLEM: "Sorunlu",
    CLOSED: "Kapanan",
    ISSUED_OPEN: "Kendi çeklerimiz — açık",
    ISSUED_CLOSED: "Kendi çeklerimiz — kapanan",
};
export const INSTRUMENT_LOCATION_KINDS = ["PORTFOLIO", "BANK", "COUNTERPARTY", "NONE"];
export const instrumentLocationKindSchema = z.enum(INSTRUMENT_LOCATION_KINDS);
export const INSTRUMENT_BATCH_TYPES = [
    "RECEIPT_FROM_CUSTOMER",
    "ENDORSE_TO_SUPPLIER",
    "TO_BANK_COLLECTION",
    "TO_BANK_COLLATERAL",
    "COLLATERAL_RETURN",
    "RETURN_TO_CUSTOMER",
    "COLLECTION",
    "BOUNCE",
    "PROTEST",
    "LEGAL_FOLLOWUP",
    "WRITE_OFF",
    "ENDORSEMENT_CLOSE",
    "ISSUE",
    "PAYMENT",
    "CANCEL",
];
export const instrumentBatchTypeSchema = z.enum(INSTRUMENT_BATCH_TYPES);
export const INSTRUMENT_BATCH_TYPE_LABELS = {
    RECEIPT_FROM_CUSTOMER: "Müşteri çek/senet giriş bordrosu",
    ENDORSE_TO_SUPPLIER: "Tedarikçiye ciro (çıkış bordrosu)",
    TO_BANK_COLLECTION: "Bankaya tahsile verme",
    TO_BANK_COLLATERAL: "Bankaya teminata verme",
    COLLATERAL_RETURN: "Teminattan geri alma",
    RETURN_TO_CUSTOMER: "Müşteriye iade",
    COLLECTION: "Tahsil",
    BOUNCE: "Karşılıksız dönüş",
    PROTEST: "Senet protestosu",
    LEGAL_FOLLOWUP: "Yasal takibe alma",
    WRITE_OFF: "Şüpheli alacaktan silme",
    ENDORSEMENT_CLOSE: "Ciro riskini kapatma",
    ISSUE: "Kendi çek/senedimizi keşide",
    PAYMENT: "Kendi çek/senedimizin ödenmesi",
    CANCEL: "Kendi çek/senedimizin iptali",
};
/** Karşılıksız / silme benzeri bordrolar ek izin (`cheques.bounce`) ister. */
export const BOUNCE_LIKE_BATCH_TYPES = ["BOUNCE", "PROTEST", "WRITE_OFF"];
export const BOUNCE_CONTROL_KEYS = ["DOUBTFUL_RECEIVABLE", "CUSTOMER_RECEIVABLE"];
export const bounceControlKeySchema = z.enum(BOUNCE_CONTROL_KEYS);
export const BOUNCE_CONTROL_KEY_LABELS = {
    DOUBTFUL_RECEIVABLE: "Şüpheli ticari alacaklar (128)",
    CUSTOMER_RECEIVABLE: "Alıcılar (120)",
};
export const CHEQUE_LEAF_STATUSES = ["UNUSED", "USED", "CANCELLED", "LOST"];
export const chequeLeafStatusSchema = z.enum(CHEQUE_LEAF_STATUSES);
export const CHEQUE_LEAF_STATUS_LABELS = {
    UNUSED: "Kullanılmadı",
    USED: "Kesildi",
    CANCELLED: "İptal",
    LOST: "Kayıp",
};
export const INSTRUMENT_WARNINGS = [
    "RISKY_DRAWER",
    "DUE_DATE_PASSED",
    "INSUFFICIENT_BANK_BALANCE",
    "NEGATIVE_CASH_BALANCE",
    "OVERDRAFT_LIMIT_EXCEEDED",
];
export const instrumentWarningSchema = z.enum(INSTRUMENT_WARNINGS);
export const INSTRUMENT_WARNING_LABELS = {
    RISKY_DRAWER: "Riskli keşideci: geçmişte karşılıksız / protesto kaydı ya da elle risk işareti var",
    DUE_DATE_PASSED: "Vadesi geçmiş çek/senet",
    INSUFFICIENT_BANK_BALANCE: "Ödeme hesabının bakiyesi (KMH dâhil) yetersiz",
    NEGATIVE_CASH_BALANCE: "Kasa bakiyesi eksiye düştü",
    OVERDRAFT_LIMIT_EXCEEDED: "Banka hesabı KMH limitini aştı",
};
// ── Ayarlar ─────────────────────────────────────────────────────────────────
export const instrumentSettingsSchema = z.object({
    companyId: uuidSchema,
    bounceReceivableKey: bounceControlKeySchema.describe("Karşılıksız / protestolu alacağın izleneceği hesap (128 ya da 120)"),
    collectionReminderDays: z.number().int().describe("Vadesine bu kadar gün kalan portföy çeki: tahsile ver"),
    ownPaymentReminderDays: z
        .number()
        .int()
        .describe("Kendi çekimizin vadesine bu kadar gün kala: bakiye kontrolü"),
    collateralReminderDays: z.number().int(),
    endorsementCloseDays: z.number().int().describe("Ciro edilen çekin riski vade + N gün sonra kapanır"),
    riskyBounceThreshold: z
        .number()
        .int()
        .describe("Keşideci bu sayıda karşılıksız / protestoda riskli sayılır"),
    version: z.number().int(),
});
export const updateInstrumentSettingsSchema = patchOf(z.object({
    bounceReceivableKey: bounceControlKeySchema,
    collectionReminderDays: z.number().int().min(0).max(60),
    ownPaymentReminderDays: z.number().int().min(0).max(30),
    collateralReminderDays: z.number().int().min(0).max(60),
    endorsementCloseDays: z.number().int().min(0).max(365),
    riskyBounceThreshold: z.number().int().min(1).max(100),
}));
// ── Karekod ─────────────────────────────────────────────────────────────────
export const QR_FORMATS = ["TR_CHEQUE", "KEY_VALUE", "UNKNOWN"];
export const qrFormatSchema = z.enum(QR_FORMATS);
export const parseQrRequestSchema = z.object({ raw: z.string().min(1).max(2000) });
export const parsedQrSchema = z.object({
    format: qrFormatSchema.describe("UNKNOWN: ham metin saklanır, alanlar elle girilir"),
    serialNo: z.string().nullable(),
    bankCode: z.string().nullable().describe("4 haneli EFT kodu"),
    bankId: uuidSchema.nullable(),
    bankName: z.string().nullable(),
    branchCode: z.string().nullable(),
    accountNo: z.string().nullable(),
    iban: z.string().nullable(),
    drawerTaxId: z.string().nullable(),
    mersisNo: z.string().nullable(),
    warnings: z.array(z.string()),
});
// ── Çek / senet ─────────────────────────────────────────────────────────────
const taxIdSchema = z
    .string()
    .trim()
    .regex(/^\d{10,11}$/, "VKN 10, TCKN 11 haneli olmalıdır")
    .nullable()
    .default(null);
/** Giriş (müşteri) ve keşide (kendi) bordrosundaki yeni çek/senet. */
export const newInstrumentSchema = z.object({
    kind: instrumentKindSchema,
    serialNo: z
        .string()
        .trim()
        .toUpperCase()
        .max(30)
        .nullable()
        .default(null)
        .describe("Çekte zorunlu; kendi çekimizde karne yaprağından gelir"),
    qrRaw: z.string().max(2000).nullable().default(null).describe("Çekin karekodundan okunan HAM metin"),
    bankId: uuidSchema.nullable().default(null),
    bankName: optionalText(200).describe("Katalogda olmayan banka için ad"),
    branchName: optionalText(120),
    branchCode: z
        .string()
        .trim()
        .regex(/^\d{1,6}$/, "Şube kodu rakamlardan oluşur")
        .nullable()
        .default(null),
    accountNo: optionalText(40),
    iban: z
        .string()
        .transform((value) => value.replace(/\s+/g, "").toUpperCase())
        .pipe(z.string().regex(/^TR\d{24}$/, "IBAN TR ile başlayan 26 karakter olmalıdır"))
        .nullable()
        .default(null),
    drawerName: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .nullable()
        .default(null)
        .describe("Kendi çekimizde şirket adı"),
    drawerTaxId: taxIdSchema,
    payeeName: optionalText(200),
    issuePlace: optionalText(120),
    issueDate: localDateSchema,
    dueDate: localDateSchema,
    amount: positiveMoneySchema,
    fxCurrency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .nullable()
        .default(null)
        .describe("Döviz alanları bilgi amaçlıdır; muhasebe TRY tutarla yapılır"),
    fxAmount: z
        .string()
        .regex(/^\d{1,16}(\.\d{1,2})?$/)
        .nullable()
        .default(null),
    fxRate: z
        .string()
        .regex(/^\d{1,12}(\.\d{1,6})?$/)
        .nullable()
        .default(null),
    frontFileId: uuidSchema.nullable().default(null),
    backFileId: uuidSchema.nullable().default(null),
    notes: optionalText(1000),
    ownBankAccountId: uuidSchema.nullable().default(null).describe("Kendi çekimiz: ödeneceği banka hesabı"),
    chequeBookLeafId: uuidSchema.nullable().default(null).describe("Kendi çekimiz: karne yaprağı"),
});
export const instrumentSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    kind: instrumentKindSchema,
    direction: instrumentDirectionSchema,
    portfolioNo: z.string().describe("PF2026000042"),
    serialNo: z.string().nullable(),
    qrRaw: z.string().nullable(),
    qrData: z.record(z.string(), z.string()).nullable(),
    bankId: uuidSchema.nullable(),
    bankName: z.string().nullable(),
    branchName: z.string().nullable(),
    branchCode: z.string().nullable(),
    accountNo: z.string().nullable(),
    iban: z.string().nullable(),
    drawerName: z.string(),
    drawerTaxId: z.string().nullable(),
    drawerKey: z.string(),
    payeeName: z.string().nullable(),
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    ownBankAccountId: uuidSchema.nullable(),
    ownBankAccountName: z.string().nullable(),
    chequeBookLeafId: uuidSchema.nullable(),
    issuePlace: z.string().nullable(),
    issueDate: localDateSchema,
    dueDate: localDateSchema,
    amount: moneySchema,
    currency: z.literal("TRY"),
    fxCurrency: z.string().nullable(),
    fxAmount: z.string().nullable(),
    fxRate: z.string().nullable(),
    extraAmount: moneySchema.describe("Protesto masrafı vb."),
    collectedAmount: moneySchema,
    remainingAmount: moneySchema.describe("tutar + masraf − tahsil edilen"),
    frontFileId: uuidSchema.nullable(),
    backFileId: uuidSchema.nullable(),
    notes: z.string().nullable(),
    status: instrumentStatusSchema,
    locationKind: instrumentLocationKindSchema,
    locationBankAccountId: uuidSchema.nullable(),
    locationCounterpartyId: uuidSchema.nullable(),
    locationLabel: z.string().describe("Portföy / Ziraat vadesiz / Deniz Petrol A.Ş."),
    bounceControlKey: bounceControlKeySchema.nullable(),
    stationId: uuidSchema.nullable(),
    lastBatchId: uuidSchema.nullable(),
    isVoid: z.boolean(),
    voidReason: z.string().nullable(),
    riskyDrawer: z.boolean(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const instrumentPageSchema = pageOf(instrumentSchema).extend({
    totalAmount: moneySchema.describe("Süzgece uyan tüm kayıtların kalan tutar toplamı"),
    totalCount: z.number().int(),
});
export const instrumentHistoryEntrySchema = z.object({
    id: uuidSchema,
    batchId: uuidSchema,
    batchNo: z.string(),
    batchType: instrumentBatchTypeSchema,
    fromStatus: instrumentStatusSchema.nullable(),
    toStatus: instrumentStatusSchema,
    amount: moneySchema,
    isRollback: z.boolean(),
    note: z.string().nullable(),
    journalEntryId: uuidSchema.nullable(),
    changedBy: z.string().nullable().describe("Kullanıcı adı; sistem işi için null"),
    changedAt: timestampSchema,
});
export const instrumentEndorsementSchema = z.object({
    id: uuidSchema,
    batchId: uuidSchema,
    fromCounterpartyId: uuidSchema.nullable(),
    fromCounterpartyName: z.string().nullable(),
    toCounterpartyId: uuidSchema,
    toCounterpartyName: z.string(),
    endorsedOn: localDateSchema,
    status: z.enum(["ACTIVE", "CLOSED", "RETURNED", "CANCELLED"]),
    closedOn: localDateSchema.nullable(),
});
export const instrumentDetailSchema = instrumentSchema.extend({
    history: z.array(instrumentHistoryEntrySchema),
    endorsements: z.array(instrumentEndorsementSchema),
    journalEntryIds: z.array(uuidSchema).describe("Çeke ait fişler (çek boyutlu satırı olan)"),
});
export const listInstrumentsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    direction: instrumentDirectionSchema.optional(),
    kind: instrumentKindSchema.optional(),
    status: instrumentStatusSchema.optional(),
    group: instrumentStatusGroupSchema.optional().describe("Durum sekmesi"),
    counterpartyId: uuidSchema.optional(),
    bankAccountId: uuidSchema.optional().describe("Bulunduğu ya da ödeneceği banka hesabı"),
    bankId: uuidSchema.optional().describe("Çekin bankası"),
    stationId: uuidSchema.optional(),
    drawerKey: z.string().max(200).optional(),
    dueFrom: localDateSchema.optional(),
    dueTo: localDateSchema.optional(),
    search: z.string().trim().min(1).max(100).optional().describe("Portföy no, seri no, keşideci, cari"),
    includeVoid: queryBoolean.optional(),
    sort: z.enum(["DUE_ASC", "DUE_DESC", "CREATED_DESC"]).default("DUE_ASC"),
});
export const updateInstrumentSchema = patchOf(z.object({
    notes: z.string().trim().max(1000).nullable(),
    frontFileId: uuidSchema.nullable(),
    backFileId: uuidSchema.nullable(),
})).extend({ version: versionSchema });
/** Mobil / web: tek görsel yükleme sonrası çeke bağlama. */
export const attachInstrumentImageSchema = z.object({
    side: z.enum(["FRONT", "BACK"]),
    fileId: uuidSchema.describe("`INSTRUMENT_IMAGE` amacıyla yüklenmiş, onaylanmış dosya"),
});
// ── Bordrolar ───────────────────────────────────────────────────────────────
const batchBase = {
    companyId: uuidSchema,
    batchDate: localDateSchema,
    description: optionalText(500).describe("Boşsa bordro türünden üretilir"),
    stationId: uuidSchema.nullable().default(null).describe("Analitik istasyon boyutu"),
};
const instrumentIdsSchema = z
    .array(uuidSchema)
    .min(1, "En az bir çek/senet seçin")
    .max(200)
    .refine((ids) => new Set(ids).size === ids.length, "Aynı çek iki kez seçilmiş");
const moneyTarget = {
    bankAccountId: uuidSchema.nullable().default(null),
    cashRegisterId: uuidSchema.nullable().default(null),
};
export const createInstrumentBatchSchema = z.discriminatedUnion("batchType", [
    z.object({
        batchType: z.literal("RECEIPT_FROM_CUSTOMER"),
        ...batchBase,
        counterpartyId: uuidSchema,
        controlKey: controlKeySchema.optional().describe("Varsayılan: carinin rolüne göre (Alıcılar 120)"),
        instruments: z.array(newInstrumentSchema).min(1).max(100),
        settlement: settlementSchema,
    }),
    z.object({
        batchType: z.literal("ISSUE"),
        ...batchBase,
        counterpartyId: uuidSchema,
        controlKey: controlKeySchema.optional().describe("Varsayılan: Satıcılar (320)"),
        instruments: z.array(newInstrumentSchema).min(1).max(100),
        settlement: settlementSchema,
    }),
    z.object({
        batchType: z.literal("ENDORSE_TO_SUPPLIER"),
        ...batchBase,
        counterpartyId: uuidSchema,
        controlKey: controlKeySchema.optional(),
        instrumentIds: instrumentIdsSchema,
        settlement: settlementSchema,
    }),
    z.object({
        batchType: z.literal("TO_BANK_COLLECTION"),
        ...batchBase,
        bankAccountId: uuidSchema,
        instrumentIds: instrumentIdsSchema,
    }),
    z.object({
        batchType: z.literal("TO_BANK_COLLATERAL"),
        ...batchBase,
        bankAccountId: uuidSchema,
        instrumentIds: instrumentIdsSchema,
    }),
    z.object({ batchType: z.literal("COLLATERAL_RETURN"), ...batchBase, instrumentIds: instrumentIdsSchema }),
    z.object({ batchType: z.literal("RETURN_TO_CUSTOMER"), ...batchBase, instrumentIds: instrumentIdsSchema }),
    z.object({
        batchType: z.literal("COLLECTION"),
        ...batchBase,
        ...moneyTarget,
        lines: z
            .array(z.object({
            instrumentId: uuidSchema,
            amount: positiveMoneySchema
                .nullable()
                .default(null)
                .describe("Boşsa kalan tutarın tamamı; kısmi tahsil yalnızca senette"),
        }))
            .min(1)
            .max(200),
        commissionAmount: nonNegativeMoneySchema.default("0").describe("Bankanın kestiği tahsil masrafı"),
    }),
    z.object({ batchType: z.literal("BOUNCE"), ...batchBase, instrumentIds: instrumentIdsSchema }),
    z.object({
        batchType: z.literal("PROTEST"),
        ...batchBase,
        ...moneyTarget,
        instrumentIds: instrumentIdsSchema,
        expenseAmount: nonNegativeMoneySchema
            .default("0")
            .describe("Protesto masrafı: alacağa eklenir, kasadan/bankadan ödenir"),
    }),
    z.object({ batchType: z.literal("LEGAL_FOLLOWUP"), ...batchBase, instrumentIds: instrumentIdsSchema }),
    z.object({ batchType: z.literal("WRITE_OFF"), ...batchBase, instrumentIds: instrumentIdsSchema }),
    z.object({ batchType: z.literal("ENDORSEMENT_CLOSE"), ...batchBase, instrumentIds: instrumentIdsSchema }),
    z.object({
        batchType: z.literal("PAYMENT"),
        ...batchBase,
        ...moneyTarget,
        instrumentIds: instrumentIdsSchema,
    }),
    z.object({ batchType: z.literal("CANCEL"), ...batchBase, instrumentIds: instrumentIdsSchema }),
]);
export const batchPostingLineSchema = z.object({
    key: z.string(),
    side: z.enum(["DEBIT", "CREDIT"]),
    amount: moneySchema,
    accountId: uuidSchema.nullable().describe("Önizlemede çözülen hesap; eşleştirme yoksa null"),
    accountCode: z.string().nullable(),
    accountName: z.string().nullable(),
    counterpartyId: uuidSchema.nullable(),
    instrumentId: uuidSchema.nullable(),
    description: z.string().nullable(),
});
export const instrumentBatchLineSchema = z.object({
    lineNo: z.number().int(),
    instrumentId: uuidSchema,
    portfolioNo: z.string(),
    kind: instrumentKindSchema,
    serialNo: z.string().nullable(),
    bankName: z.string().nullable(),
    drawerName: z.string(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    dueDate: localDateSchema,
    amount: moneySchema,
    fromStatus: instrumentStatusSchema.nullable(),
    toStatus: instrumentStatusSchema,
});
export const instrumentBatchSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    batchNo: z.string().describe("BR2026000007"),
    batchType: instrumentBatchTypeSchema,
    batchDate: localDateSchema,
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    controlKey: z.string().nullable(),
    bankAccountId: uuidSchema.nullable(),
    bankAccountName: z.string().nullable(),
    cashRegisterId: uuidSchema.nullable(),
    cashRegisterName: z.string().nullable(),
    totalAmount: moneySchema,
    lineCount: z.number().int(),
    commissionAmount: moneySchema,
    expenseAmount: moneySchema,
    description: z.string(),
    stationId: uuidSchema.nullable(),
    treasuryTxnId: uuidSchema.nullable(),
    status: z.enum(["POSTED", "CANCELLED"]),
    ledgerStatus: z.enum(["POSTED", "QUEUED", "NONE"]),
    journalEntryId: uuidSchema.nullable(),
    cancelReason: z.string().nullable(),
    cancelledAt: timestampSchema.nullable(),
    createdBy: z.string().nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
    lines: z.array(instrumentBatchLineSchema),
});
export const instrumentBatchResultSchema = z.object({
    batch: instrumentBatchSchema,
    warnings: z.array(instrumentWarningSchema),
    unallocatedAmount: moneySchema.nullable().describe("Giriş/keşide/ciro: açık kalemlere uygulanamayan tutar"),
});
export const instrumentBatchPreviewSchema = z.object({
    batchType: instrumentBatchTypeSchema,
    totalAmount: moneySchema,
    lines: z.array(instrumentBatchLineSchema.omit({ instrumentId: true, portfolioNo: true }).extend({
        instrumentId: uuidSchema.nullable(),
        portfolioNo: z.string().nullable(),
    })),
    posting: z.array(batchPostingLineSchema).describe("Kesilecek fişin satırları (boş: fiş üretmez)"),
    missingMappings: z.array(z.string()).describe("Eşleştirmesi olmayan anahtarlar: fiş kuyruğa düşer"),
    warnings: z.array(instrumentWarningSchema),
});
export const instrumentBatchSummarySchema = instrumentBatchSchema.omit({ lines: true });
export const instrumentBatchPageSchema = pageOf(instrumentBatchSummarySchema);
export const listInstrumentBatchesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    batchType: instrumentBatchTypeSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    dateFrom: localDateSchema.optional(),
    dateTo: localDateSchema.optional(),
    includeCancelled: queryBoolean.optional(),
});
export const cancelInstrumentBatchSchema = z.object({ reason: reasonSchema, version: versionSchema });
// ── Çek karneleri ───────────────────────────────────────────────────────────
export const createChequeBookSchema = z.object({
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    serialPrefix: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9]{0,6}$/, "Seri harfi en çok 6 harf/rakam")
        .default(""),
    firstNo: z.number().int().positive(),
    lastNo: z.number().int().positive(),
    digits: z.number().int().min(1).max(12).default(7),
    receivedOn: localDateSchema,
    notes: optionalText(500),
});
export const chequeBookSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    bankAccountId: uuidSchema,
    bankAccountName: z.string(),
    serialPrefix: z.string(),
    firstNo: z.number().int(),
    lastNo: z.number().int(),
    digits: z.number().int(),
    firstSerial: z.string(),
    lastSerial: z.string(),
    receivedOn: localDateSchema,
    notes: z.string().nullable(),
    isActive: z.boolean(),
    counts: z.object({
        UNUSED: z.number().int(),
        USED: z.number().int(),
        CANCELLED: z.number().int(),
        LOST: z.number().int(),
    }),
    version: z.number().int(),
});
export const chequeBookListSchema = z.object({ items: z.array(chequeBookSchema) });
export const listChequeBooksQuerySchema = z.object({
    companyId: uuidSchema,
    bankAccountId: uuidSchema.optional(),
    includeInactive: queryBoolean.optional(),
});
export const updateChequeBookSchema = patchOf(z.object({ notes: z.string().trim().max(500).nullable(), isActive: z.boolean() })).extend({ version: versionSchema });
export const chequeLeafSchema = z.object({
    id: uuidSchema,
    bookId: uuidSchema,
    bankAccountId: uuidSchema,
    serialNo: z.string(),
    status: chequeLeafStatusSchema,
    instrumentId: uuidSchema.nullable(),
    instrumentPortfolioNo: z.string().nullable(),
    instrumentAmount: moneySchema.nullable(),
    instrumentDueDate: localDateSchema.nullable(),
    note: z.string().nullable(),
    updatedAt: timestampSchema,
});
export const chequeLeafListSchema = z.object({ items: z.array(chequeLeafSchema) });
export const listChequeLeavesQuerySchema = z.object({ status: chequeLeafStatusSchema.optional() });
export const unusedChequeLeavesQuerySchema = z.object({ companyId: uuidSchema, bankAccountId: uuidSchema });
export const changeChequeLeafStatusSchema = z.object({
    status: z.enum(["CANCELLED", "LOST"]).describe("Yalnızca kullanılmamış yaprak iptal / kayıp işaretlenir"),
    note: z.string().trim().min(3).max(500),
});
// ── Keşideci riski ──────────────────────────────────────────────────────────
export const drawerRiskSchema = z.object({
    drawerKey: z.string(),
    drawerName: z.string(),
    drawerTaxId: z.string().nullable(),
    inPortfolio: moneySchema.describe("Portföy + bankada (tahsil / teminat)"),
    endorsedOpen: moneySchema.describe("Ciro edilmiş, riski kapanmamış"),
    problemOpen: moneySchema.describe("Karşılıksız / protestolu / yasal takipte kalan"),
    totalExposure: moneySchema,
    instrumentCount: z.number().int(),
    bounceCount: z.number().int().describe("Geçmişteki karşılıksız + geri gelen + protesto sayısı"),
    isRisky: z.boolean(),
    flagged: z.boolean().nullable().describe("Elle işaret: true riskli, false güvenilir, null işaret yok"),
    flagNote: z.string().nullable(),
});
export const drawerRiskListSchema = z.object({ items: z.array(drawerRiskSchema) });
export const drawerRiskQuerySchema = z.object({
    companyId: uuidSchema,
    riskyOnly: queryBoolean.optional(),
    search: z.string().trim().min(1).max(100).optional(),
});
export const setDrawerFlagSchema = z.object({
    companyId: uuidSchema,
    drawerKey: z.string().min(1).max(200),
    drawerName: z.string().trim().min(1).max(200),
    isRisky: z.boolean().nullable().describe("null: işareti kaldır"),
    note: optionalText(500),
});
// ── Raporlar ────────────────────────────────────────────────────────────────
const statusTotalSchema = z.object({
    status: instrumentStatusSchema,
    kind: instrumentKindSchema,
    direction: instrumentDirectionSchema,
    count: z.number().int(),
    amount: moneySchema.describe("Kalan tutar"),
});
export const portfolioReportSchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema,
    rows: z.array(statusTotalSchema),
    receivableOpen: moneySchema.describe("Elimizdeki + bankadaki alınan çek/senet"),
    endorsedOpen: moneySchema,
    problemOpen: moneySchema,
    issuedOpen: moneySchema.describe("Ödenmemiş kendi çek/senetlerimiz"),
});
export const MATURITY_BUCKETS = ["OVERDUE", "D0_7", "D8_30", "D31_60", "D61_90", "D90_PLUS"];
export const maturityBucketSchema = z.enum(MATURITY_BUCKETS);
export const MATURITY_BUCKET_LABELS = {
    OVERDUE: "Vadesi geçmiş",
    D0_7: "0–7 gün",
    D8_30: "8–30 gün",
    D31_60: "31–60 gün",
    D61_90: "61–90 gün",
    D90_PLUS: "90 gün üzeri",
};
export const maturityReportSchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema,
    rows: z.array(z.object({
        bucket: maturityBucketSchema,
        receivableCount: z.number().int(),
        receivableAmount: moneySchema,
        payableCount: z.number().int(),
        payableAmount: moneySchema,
    })),
});
export const instrumentCalendarQuerySchema = z.object({
    companyId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    direction: instrumentDirectionSchema.optional(),
    bankAccountId: uuidSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
});
export const instrumentCalendarSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    days: z.array(z.object({
        date: localDateSchema,
        receivableCount: z.number().int(),
        receivableAmount: moneySchema,
        payableCount: z.number().int(),
        payableAmount: moneySchema,
    })),
});
export const issuedPaymentPlanSchema = z.object({
    companyId: uuidSchema,
    items: z.array(z.object({
        bankAccountId: uuidSchema.nullable(),
        bankAccountName: z.string().nullable(),
        dueDate: localDateSchema,
        count: z.number().int(),
        amount: moneySchema,
        cumulativeAmount: moneySchema.describe("Aynı hesapta bu güne kadarki ödemelerin toplamı"),
        availableBalance: moneySchema.nullable().describe("Bugünkü bakiye + KMH limiti"),
        shortfall: moneySchema.describe("Birikimli ödeme kullanılabilir bakiyeyi aşarsa fark"),
    })),
});
export const counterpartyInstrumentRiskSchema = z.object({
    companyId: uuidSchema,
    items: z.array(z.object({
        counterpartyId: uuidSchema,
        counterpartyName: z.string(),
        inPortfolio: moneySchema,
        endorsedOpen: moneySchema,
        problemOpen: moneySchema,
        bounceCount: z.number().int(),
        totalExposure: moneySchema,
    })),
});
export const instrumentLedgerReconciliationSchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema,
    balanced: z.boolean(),
    rows: z.array(z.object({
        postingKey: z.string(),
        label: z.string(),
        accountCode: z.string().nullable(),
        subledgerBalance: moneySchema.describe("Portföydeki çek/senetlerin tutarı (alt defter)"),
        ledgerBalance: moneySchema.describe("Hesabın defter bakiyesi (çek boyutlu satırlar)"),
        difference: moneySchema,
    })),
});
export const instrumentReportQuerySchema = z.object({
    companyId: uuidSchema,
    asOf: localDateSchema.optional().describe("Varsayılan: bugün (Europe/Istanbul)"),
});
//# sourceMappingURL=instruments.js.map